import { useQuery } from '@tanstack/react-query';
import config from 'api/config';
import { ApiServices } from 'context/ApiServicesContext';
import { isFuture } from 'date-fns';
import { ServiceLineSide } from 'types/models';
import { Material } from 'types/models/inventory';
import { lcrTiers, poolStatuses, samplePoolCategories, siteTypes } from 'types/models/samples';
import { CONTACT_TYPES, RIGHT_OF_ENTRY_STATUS_CONSTANTS } from 'utils/constants';

export class OptionsService {
  private readonly apiServices: ApiServices;

  constructor(services: ApiServices) {
    this.apiServices = services;
  }

  getMaterialOptions(options?: { valuePath?: 'id' | 'code'; filter?: (mat: Material) => boolean }) {
    const valuePath = options?.valuePath ?? 'id';
    const filterMatOptions = options?.filter ?? ((_) => true);

    const { replacementService } = this.apiServices;
    return useQuery(['Materials'], () => replacementService.getMaterials(), {
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
      select(materials) {
        return materials.filter(filterMatOptions).map(({ description, ...material }) => ({
          value: material[valuePath],
          label: description,
        }));
      },
    });
  }

  getLeadStatusOptions(valuePath: 'Id' | 'Status' = 'Status') {
    const { inventoryService } = this.apiServices;
    return useQuery(['LeadStatuses'], () => inventoryService.getLeadStatusTypes(), {
      select(leadStatuses) {
        return leadStatuses.map((leadStatus) => ({
          value: leadStatus[valuePath] ?? leadStatus.Status,
          label: leadStatus.Status,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getReplacementStatusOptions() {
    const { replacementService } = this.apiServices;
    return useQuery(['replacementStatuses'], () => replacementService.getReplacementStatuses(), {
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getReplacementContractOptions() {
    const { replacementService } = this.apiServices;
    return useQuery(['replacementContracts'], () => replacementService.getReplacementContracts(), {
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getServiceLineDiameterOptions() {
    const { replacementService } = this.apiServices;
    return useQuery(['ServiceLineDiameters'], () => replacementService.getServiceLineDiameters(), {
      select(diameters) {
        return diameters
          .map(({ diameter, fraction }) => ({
            value: diameter,
            label: fraction ?? 'Unknown',
          }))
          .sort((a, b) => (a.value > b.value ? 1 : -1));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getInspectionTypeOptions(side?: ServiceLineSide) {
    const { inspectionsService } = this.apiServices;
    return useQuery(['InspectionTypes'], () => inspectionsService.getInspectionTypes(), {
      select(inspectionTypes) {
        return inspectionTypes
          .filter((it) => !side || it.ServiceLineSide === side)
          .map(({ Id, Value }) => ({ value: Id, label: Value }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getInspectionMethodOptions() {
    const { inspectionsService } = this.apiServices;
    return useQuery(['InspectionMethods'], () => inspectionsService.getInspectionMethods(), {
      select(inspectionMethods) {
        return inspectionMethods.map(({ Id, Code }) => ({
          value: Id,
          label: Code,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getInspectionLocationOptions(side?: ServiceLineSide) {
    const { inspectionsService } = this.apiServices;
    return useQuery(['InspectionLocations'], () => inspectionsService.getInspectionLocations(), {
      select(inspectionLocations) {
        return inspectionLocations
          .filter((it) => !side || it.ServiceLineSide === side)
          .map(({ Id, Value }) => ({ value: Id, label: Value }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getBuildingTypeOptions(options?: {
    valuePath?: 'Id' | 'Code' | 'Description';
    schoolAndDaycareOnly?: boolean;
  }) {
    const { inventoryService } = this.apiServices;
    const valuePath = options?.valuePath ?? 'Code';
    return useQuery(['BuildingTypes'], () => inventoryService.getBuildingTypes(), {
      select(buildingTypes) {
        return buildingTypes
          .filter((bt) => !options?.schoolAndDaycareOnly || bt.IsSchoolOrDaycare)
          .map((bt) => ({
            id: bt['Id'],
            value: bt[valuePath] ?? bt.Code,
            label: bt.Description,
          }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getPropertyOwnershipStatusOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['OwnershipStatuses'], () => inventoryService.getPropertyOwnershipStatuses(), {
      select(options) {
        return options.map(({ Name }) => ({
          value: Name,
          label: Name,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getStateOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['States'], () => inventoryService.getStates(), {
      select(states) {
        return states.map(({ Code, Name }) => ({
          value: Code,
          label: Name,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getZoneOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['Zones'], () => inventoryService.getZones(), {
      select(zoneTypes) {
        return zoneTypes.map((zone) => ({ value: zone, label: zone }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getContractOptions(onlyActive = false) {
    const { inventoryService } = this.apiServices;
    return useQuery(['Contracts'], () => inventoryService.getInventoryContracts(), {
      select(contracts) {
        return contracts
          .filter((c) => !onlyActive || isFuture(new Date(c.endDate)))
          .map(({ id, name }) => ({ value: id, label: name }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getContractorOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['Contractors'], () => inventoryService.getInventoryContractors(), {
      select(contractors) {
        return contractors.map(({ id, name }) => ({ value: id, label: name }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getServiceLineTypeOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['ServiceLineTypes'], () => inventoryService.getServiceLineTypes(), {
      select(serviceLineTypes) {
        return serviceLineTypes.map(({ id, value }) => ({ value: id, label: value }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getMainDiameterOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['MainDiameters'], () => inventoryService.getMainDiameters(), {
      select(mainDiameters) {
        return mainDiameters.map(({ Diameter, DiameterStr }) => ({
          value: Diameter,
          label: DiameterStr,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getServiceLineMaterialMethodOptions(valuePath: 'Id' | 'Code' | 'Description' = 'Id') {
    const { inventoryService } = this.apiServices;
    return useQuery(
      ['ServiceLineMaterialMethods'],
      () => inventoryService.getServiceLineMaterialMethods(),
      {
        select(methods) {
          return methods.map((method) => ({
            value: method[valuePath] ?? method.Id,
            label: method.Description,
          }));
        },
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getLifecycleStatusOptions(valuePath: 'Code' | 'Value' | 'Description' = 'Code') {
    const { inventoryService } = this.apiServices;
    return useQuery(['LifecycleStatuses'], () => inventoryService.getLifecycleStatuses(), {
      select(LifecycleStatuses) {
        return LifecycleStatuses.map((status) => ({
          value: status[valuePath] ?? status.Code,
          label: status.Value,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getSchoolAndDaycareFacilityTypeOptions(valuePath: 'id' | 'name' = 'id') {
    const { samplesService } = this.apiServices;
    return useQuery(
      ['SchoolAndDaycareFacilityTypeOptions'],
      () => samplesService.getFacilityTypes(),
      {
        select(statuses) {
          return statuses.map((status) => ({
            value: valuePath === 'id' ? String(status.id) : status[valuePath],
            label: status.name,
          }));
        },
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getSamplePoolStatusOptions() {
    return useQuery(
      ['SamplePoolStatuses'],
      () =>
        poolStatuses.map((status) => ({
          value: status,
          label: status,
        })),
      {
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getSamplePoolSiteTypeOptions() {
    return useQuery(
      ['SamplePoolSiteTypes'],
      () =>
        siteTypes.map((st) => ({
          value: st,
          label: st,
        })),
      {
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getLCRTierOptions() {
    return useQuery(
      ['SamplePoolLCRTiers'],
      () =>
        lcrTiers.map((tier) => ({
          value: tier,
          label: tier,
        })),
      {
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getSamplePoolCategoryOptions() {
    return useQuery(
      ['SamplePoolCategories'],
      () =>
        samplePoolCategories.map((category) => ({
          value: category,
          label: category,
        })),
      {
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getPWSOptions() {
    const { inventoryService } = this.apiServices;
    return useQuery(['PWSOptions'], () => inventoryService.getPwsIds(), {
      select(pwsIds) {
        return pwsIds.map((pwsId) => ({
          value: pwsId,
          label: pwsId,
        }));
      },
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      placeholderData: [],
    });
  }

  getContactTypeOptions() {
    const contactTypeOptions = Object.values(CONTACT_TYPES).map((contactType) => ({
      label: contactType,
      value: contactType,
    }));

    return {
      data: contactTypeOptions,
    };
  }

  getRightOfEntryStatusOptions(manualROEApprovalRequired: boolean = false) {
    const rightOfEntryStatusOptions = Object.values(RIGHT_OF_ENTRY_STATUS_CONSTANTS)
      .map((status) => ({
        label: status,
        value: status,
      }))
      .filter(
        (status) =>
          manualROEApprovalRequired ||
          status.value !== RIGHT_OF_ENTRY_STATUS_CONSTANTS.PENDING_REVIEW
      );

    return {
      data: rightOfEntryStatusOptions,
    };
  }

  getCommunicationProgramOptions() {
    const { communicationService } = this.apiServices;
    return useQuery(
      ['CommunicationPrograms'],
      () => communicationService.getCommunicationPrograms(),
      {
        select(programs) {
          return programs.map(({ id, programName }) => ({ value: id, label: programName }));
        },
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getCommunicationMethodOptions() {
    const { communicationService } = this.apiServices;
    return useQuery(
      ['CommunicationMethods'],
      () => communicationService.getCommunicationMethods(),
      {
        select(methods) {
          return methods.map(({ id, method }) => ({ value: id, label: method }));
        },
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getCommunicationResponseTypeOptions() {
    const { communicationService } = this.apiServices;
    return useQuery(
      ['CommunicationResponses'],
      () => communicationService.getCommunicationResponseTypes(),
      {
        select(responses) {
          return responses.map(
            ({ id, responseType, communicationProgramId, affirmativeResponse }) => ({
              value: id,
              label: responseType,
              communicationProgramId,
              affirmativeResponse,
            })
          );
        },
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getCommunicationRefusalReasonOptions() {
    const { communicationService } = this.apiServices;
    return useQuery(
      ['CommunicationRefusalReasons'],
      () => communicationService.getCommunicationRefusalReasons(),
      {
        select(reasons) {
          return reasons.map(({ id, refusalReason, communicationProgramId }) => ({
            value: id,
            label: refusalReason,
            communicationProgramId,
          }));
        },
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  getYears(earliestYear, latestYear = new Date().getFullYear()) {
    return useQuery(
      ['YearRange', earliestYear, latestYear],
      () => {
        return Array.from(new Array(latestYear - earliestYear + 1), (v, i) => latestYear - i);
      },
      {
        cacheTime: config.cacheTime,
        staleTime: Infinity,
        placeholderData: [],
      }
    );
  }

  getTapSampleLocationOptions() {
    const { samplesService } = this.apiServices;
    return useQuery(['TapSampleLocations'], () => samplesService.getWaterSampleLocations(), {
      select(locations) {
        return locations.map(({ id, name }) => ({
          value: id,
          label: name,
        }));
      },
    });
  }

  getStrataOptions() {
    const { mlService } = this.apiServices;
    return useQuery(['StrataOptions'], () => mlService.getStrata(), {
      select(strata) {
        return strata.map((stratum) => ({
          value: stratum,
          label: stratum,
        }));
      },
      staleTime: config.cacheTime,
    });
  }
}
